<template>
  <section class="bg-t1-bg-light py-12 md:py-20" aria-labelledby="reviews-heading-t1">
    <div class="max-w-6xl mx-auto px-4 sm:px-6">
      <div class="text-center mb-12 md:mb-16">
        <h2 id="reviews-heading-t1" class="text-3xl font-bold tracking-tight mb-2 text-t1-neutral-dark">{{ $t('public.guestExperiences') }}</h2>
        <p class="text-t1-neutral-muted">{{ $t('public.reviewsSubtitle') }}</p>
      </div>
      <template v-if="feedbacks.length">
        <!-- Single feedback: no Swiper -->
        <div v-if="feedbacks.length <= 1" class="rms-reviews-slider relative">
          <div class="rms-reviews-slider__track min-h-[220px] md:min-h-[200px] relative">
            <div
              class="rms-reviews-slider__card bg-white p-8 border border-t1-border rounded-lg w-full"
            >
              <div class="flex mb-4 gap-0.5" aria-hidden="true">
                <span v-for="s in 5" :key="s" class="material-icons text-lg" :style="s <= starCount(feedbacks[0]) ? starsStyle : { color: 'var(--tw-slate-300, #cbd5e1)' }">star</span>
              </div>
              <p class="italic text-t1-neutral-dark mb-6 leading-relaxed">"{{ feedbacks[0].text || '' }}"</p>
              <p class="text-sm font-bold uppercase tracking-widest text-t1-neutral-dark">— {{ feedbacks[0].name || 'Anonymous' }}</p>
            </div>
          </div>
        </div>
        <!-- Multiple: Swiper with pagination (no arrow buttons) -->
        <div v-else class="rms-reviews-slider relative">
          <Swiper
            :modules="swiperModules"
            effect="fade"
            :fade-effect="{ crossFade: true }"
            :slides-per-view="1"
            :space-between="0"
            :speed="reducedMotion ? 0 : 350"
            :autoplay="autoplayConfig"
            :pagination="paginationConfig"
            :a11y="a11yConfig"
            class="rms-reviews-swiper min-h-[220px] md:min-h-[200px]"
            @swiper="onSwiper"
          >
            <SwiperSlide v-for="(fb, idx) in feedbacks" :key="idx">
              <div
                class="rms-reviews-slider__card bg-white p-8 border border-t1-border rounded-lg w-full min-h-[220px] md:min-h-[200px] flex flex-col"
              >
                <div class="flex mb-4 gap-0.5" aria-hidden="true">
                  <span v-for="s in 5" :key="s" class="material-icons text-lg" :style="s <= starCount(fb) ? starsStyle : { color: 'var(--tw-slate-300, #cbd5e1)' }">star</span>
                </div>
                <p class="italic text-t1-neutral-dark mb-6 leading-relaxed flex-1">"{{ fb.text || '' }}"</p>
                <p class="text-sm font-bold uppercase tracking-widest text-t1-neutral-dark">— {{ fb.name || 'Anonymous' }}</p>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </template>
      <p v-else class="text-center text-t1-neutral-muted">{{ emptyMessage }}</p>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectFade, Autoplay, Pagination, A11y } from 'swiper/modules'
import { useReducedMotion } from '@/composables/useReducedMotion'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'

const props = defineProps({
  feedbacks: { type: Array, default: () => [] },
  primaryColor: { type: String, default: '' },
  emptyMessage: { type: String, default: 'No reviews yet. Be the first to leave feedback below.' },
})

const reducedMotion = useReducedMotion()

const swiperModules = [EffectFade, Autoplay, Pagination, A11y]

const starsStyle = computed(() => ({ color: props.primaryColor || '#1152d4' }))

const autoplayConfig = computed(() => {
  if (reducedMotion.value || props.feedbacks.length <= 1) return false
  return { delay: 5000, disableOnInteraction: false }
})

const paginationConfig = computed(() => {
  if (props.feedbacks.length <= 1) return false
  return {
    clickable: true,
    bulletClass: 'rms-reviews-slider__dot',
    bulletActiveClass: 'rms-reviews-slider__dot--active',
  }
})

const a11yConfig = computed(() => ({
  paginationBulletMessage: `Review {{index}} of ${props.feedbacks.length}`,
}))

function starCount(fb) {
  const r = Number(fb?.rating)
  return Math.min(5, Math.max(0, Number.isNaN(r) ? 0 : Math.round(r)))
}

function onSwiper(swiper) {
  if (swiper.pagination?.el) {
    swiper.pagination.el.setAttribute('role', 'tablist')
    swiper.pagination.el.setAttribute('aria-label', 'Review slides')
  }
  function setBulletA11y() {
    if (!swiper.pagination?.bullets) return
    swiper.pagination.bullets.forEach((bullet, i) => {
      bullet.setAttribute('role', 'tab')
      bullet.setAttribute('aria-selected', i === swiper.activeIndex ? 'true' : 'false')
    })
  }
  setBulletA11y()
  swiper.on('slideChange', setBulletA11y)
}
</script>
